import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Container, Dialog, DialogContent, makeStyles, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { Footer } from '../components/Footer';

// Importar las imágenes desde src/images/lineamientos/
import sobornoImage from '../images/lineamientos/soborno.png';
import acosoLaboralImage from '../images/lineamientos/acoso-laboral.png';
import acosoSexualImage from '../images/lineamientos/acososexual.png';
import conflictoImage from '../images/lineamientos/conflicto.png';
import analyticsService from '../API/analyticsService';

const lineamientos = [
  {
    id: 'soborno',
    title: 'Actos de soborno',
    imagePath: sobornoImage,
    event: 'BTN_INFO_ACTOS_SOBORNO',
  },
  {
    id: 'acoso-laboral',
    title: 'Acoso laboral',
    imagePath: acosoLaboralImage,
    event: 'BTN_INFO_ACOSO_LABORAL',
  },
  {
    id: 'acoso-sexual',
    title: 'Acoso sexual',
    imagePath: acosoSexualImage,
    event: 'BTN_INFO_ACOSO_SEXUAL',
  },
  {
    id: 'conflicto-interes',
    title: 'Conflicto de interés',
    imagePath: conflictoImage,
    event: 'BTN_INFO_CONFLICTO_INTERES',
  },
];

export const LineamientosScreen = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [openModal, setOpenModal] = useState(false);
  const [selectedLineamiento, setSelectedLineamiento] = useState(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [touchStartTime, setTouchStartTime] = useState(0);
  const [touchStartPos, setTouchStartPos] = useState({ x: 0, y: 0 });

  const handleVerLineamientos = lineamiento => {
    analyticsService.sendEvent(lineamiento.event);
    setSelectedLineamiento(lineamiento);
    setOpenModal(true);
    setIsZoomed(false);
    setImagePosition({ x: 0, y: 0 });
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedLineamiento(null);
    setIsZoomed(false);
    setImagePosition({ x: 0, y: 0 });
  };

  const handleImageContextMenu = event => {
    event.preventDefault();
  };

  const handleImageClick = () => {
    if (isMobile && !isDragging) {
      setIsZoomed(!isZoomed);
      if (!isZoomed) {
        setImagePosition({ x: 0, y: 0 });
      }
    }
  };

  const handleTouchStart = (e) => {
    if (isMobile) {
      const touch = e.touches[0];
      const now = Date.now();
      setTouchStartTime(now);
      const touchX = touch.clientX || touch.pageX;
      const touchY = touch.clientY || touch.pageY;
      setTouchStartPos({ x: touchX, y: touchY });

      if (isZoomed) {
        setIsDragging(true);
        setDragStart({
          x: touchX - imagePosition.x,
          y: touchY - imagePosition.y,
        });
      }
    }
  };

  const handleTouchMove = (e) => {
    if (isMobile) {
      // Si hay múltiples toques, es un gesto de pellizco - permitir zoom nativo
      if (e.touches.length > 1) {
        return; // No interferir con el zoom nativo
      }

      const touch = e.touches[0];
      const touchX = touch.clientX || touch.pageX;
      const touchY = touch.clientY || touch.pageY;
      const moveX = Math.abs(touchX - touchStartPos.x);
      const moveY = Math.abs(touchY - touchStartPos.y);

      // Si la imagen está zoomed y hay movimiento, arrastrar
      if (isZoomed && (moveX > 5 || moveY > 5)) {
        e.preventDefault();
        setIsDragging(true);
        const newX = touchX - dragStart.x;
        const newY = touchY - dragStart.y;

        // Limitar el movimiento para que la imagen no se salga demasiado
        const maxX = window.innerWidth * 0.3;
        const maxY = window.innerHeight * 0.3;

        setImagePosition({
          x: Math.max(-maxX, Math.min(maxX, newX)),
          y: Math.max(-maxY, Math.min(maxY, newY)),
        });
      }
    }
  };

  const handleTouchEnd = (e) => {
    if (isMobile) {
      const timeDiff = Date.now() - touchStartTime;
      const touch = e.changedTouches[0];
      const touchX = touch.clientX || touch.pageX;
      const touchY = touch.clientY || touch.pageY;
      const moveX = Math.abs(touchX - touchStartPos.x);
      const moveY = Math.abs(touchY - touchStartPos.y);

      // Si fue un tap rápido sin movimiento significativo, hacer zoom
      if (!isZoomed && timeDiff < 300 && moveX < 10 && moveY < 10) {
        setIsZoomed(true);
        setImagePosition({ x: 0, y: 0 });
      } else if (isZoomed && timeDiff < 300 && moveX < 10 && moveY < 10 && !isDragging) {
        // Si está zoomed y fue un tap sin arrastrar, quitar zoom
        setIsZoomed(false);
        setImagePosition({ x: 0, y: 0 });
      }

      setIsDragging(false);
    }
  };

  return (
    <Container maxWidth="lg" className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h5" component="h1" className={classes.headerTitle}>
          Lineamientos de ética
        </Typography>
      </Box>

      <Box className={classes.instructionBar}>
        <Typography className={classes.instructionText}>
          Haz clic en los botones para conocer nuestros lineamientos sobre comportamientos inapropiados
        </Typography>
      </Box>

      <Box className={classes.cardsContainer}>
        {lineamientos.map(lineamiento => (
          <Box key={lineamiento.id} className={classes.card}>
            <Typography variant="h6" component="h2" className={classes.cardTitle}>
              {lineamiento.title}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              className={classes.cardButton}
              onClick={() => handleVerLineamientos(lineamiento)}
              disableElevation
            >
              Ver lineamiento
            </Button>
          </Box>
        ))}
      </Box>

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth={false}
        fullWidth={false}
        fullScreen={isMobile}
        PaperProps={{
          className: classes.dialogPaper,
        }}
        BackdropProps={{
          style: {
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
          },
        }}
      >
        <DialogContent className={`${classes.dialogContent} ${isZoomed ? 'contentZoomed' : ''}`}>
          {selectedLineamiento && (
            <Box
              className={`${classes.imageContainer} ${isZoomed ? classes.imageZoomed : ''}`}
              onClick={handleImageClick}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              style={isZoomed && isMobile ? {
                transform: `translate(${imagePosition.x}px, ${imagePosition.y}px)`,
                transition: isDragging ? 'none' : 'transform 0.2s ease-out',
              } : {}}
            >
              <img
                src={selectedLineamiento.imagePath}
                alt={selectedLineamiento.title}
                className={`${classes.modalImage} ${isZoomed ? classes.modalImageZoomed : ''}`}
                onContextMenu={handleImageContextMenu}
                draggable={false}
                onError={e => {
                  // Muestra un mensaje de error si la imagen no se encuentra
                  e.target.style.display = 'none';
                  const errorDiv = document.createElement('div');
                  errorDiv.textContent = 'Imagen no encontrada. Por favor, verifica que la imagen existe en la ruta especificada.';
                  errorDiv.style.padding = '20px';
                  errorDiv.style.textAlign = 'center';
                  errorDiv.style.color = '#666';
                  e.target.parentNode.appendChild(errorDiv);
                }}
              />
            </Box>
          )}
        </DialogContent>
        <Box className={classes.dialogFooter}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleCloseModal}
            className={classes.closeDialogButton}
          >
            Cerrar
          </Button>
        </Box>
      </Dialog>

      <Box className={classes.footerContainer}>
        <Footer />
        <Link to="/" style={{ textDecoration: 'none' }}>
          <Button variant="outlined" color="primary" className={classes.backButton}>
            Regresar
          </Button>
        </Link>
      </Box>
    </Container>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(1, '16px'),
    borderRadius: 4,
  },
  headerTitle: {
    fontWeight: 'bold',
    textAlign: 'left',
  },
  instructionBar: {
    backgroundColor: '#273138',
    color: '#fff',
    padding: theme.spacing('12px', '16px'),
    borderRadius: '8px 8px 0px 0px',
    marginTop: theme.spacing(2),

  },
  instructionText: {
    fontSize: '0.9rem',
    textAlign: 'left',
    fontWeight: 'bold',
  },
  cardsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: theme.spacing(2),
    backgroundColor: '#fff',
    padding: theme.spacing(3),
    borderRadius: '0px 0px 8px 8px',
    boxShadow: 'rgba(0, 0, 0, 0.05) 0px 1px 2px 0px',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',

    padding: theme.spacing(2),
    border: '1px solid rgba(224, 224, 224, 0.29)',
    boxShadow: 'rgba(35, 35, 34, 0.05) 0px 2px 8px 0px',
    borderRadius: 8,
    backgroundColor: '#fff',
    minHeight: '140px',
    transition: 'box-shadow 0.3s ease',
    '&:hover': {
      boxShadow: 'rgba(35, 35, 34, 0.24) 0px 2px 16px 0px',
      border: '1px solid rgba(224, 224, 224, 0.68)',
    },
  },
  cardTitle: {
    fontWeight: 'bold',
    textAlign: 'center',
    color: '#000',
    fontSize: '20px',
    paddingBottom: '12px'
  },
  cardButton: {
    textTransform: 'none',
    fontWeight: 'bold',
    minWidth: '150px',
    transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
    '&:hover': {
      backgroundColor: '#E35A03',
    },
  },
  footerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2, 0),
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  backButton: {
    textTransform: 'none',
    borderWidth: 1,
    backgroundColor: '#fff',
    '&:hover': {
      borderWidth: 1,
    },
  },
  dialogPaper: {
    borderRadius: 16,
    boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
    margin: '32px ',
    maxHeight: 'calc(100vh - 64px)',
    display: 'flex',
    flexDirection: 'column',
    width: 'fit-content',
    maxWidth: '90vw',
    minWidth: 'auto',
    [theme.breakpoints.down('sm')]: {
      borderRadius: 0,
      margin: 0,
      maxHeight: '100vh',
      maxWidth: '100vw',
      width: '100vw',
    },
  },
  dialogContent: {
    padding: '0 1rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    overflowY: 'auto',
    overflowX: 'hidden',
    flex: 1,
    minHeight: 0,
    [theme.breakpoints.down('sm')]: {
      padding: 0,
      overflow: 'hidden',
      position: 'relative',
      WebkitOverflowScrolling: 'touch',
    },
    '&.contentZoomed': {
      [theme.breakpoints.down('sm')]: {
        overflow: 'auto',
      },
    },
  },
  imageContainer: {
    display: 'inline-block',
    width: 'auto',
    maxWidth: '100%',
    position: 'relative',
    cursor: 'pointer',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      overflow: 'hidden',
      touchAction: 'pan-x pan-y pinch-zoom',
    },
    '&.imageZoomed': {
      [theme.breakpoints.down('sm')]: {
        cursor: 'grab',
        '&:active': {
          cursor: 'grabbing',
        },
      },
    },
  },
  modalImage: {
    width: 'auto',
    maxWidth: '100%',
    height: 'auto',
    display: 'block',
    userSelect: 'none',
    objectFit: 'contain',
    WebkitUserSelect: 'none',
    WebkitTouchCallout: 'none',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      height: 'auto',
      maxHeight: 'calc(100vh - 120px)',
      objectFit: 'contain',
      touchAction: 'pan-x pan-y pinch-zoom',
    },
    '&.modalImageZoomed': {
      [theme.breakpoints.down('sm')]: {
        width: '200%',
        maxWidth: '200%',
        height: 'auto',
        maxHeight: 'none',
        transformOrigin: 'center center',
      },
    },
  },
  zoomHint: {
    position: 'absolute',
    bottom: theme.spacing(2),
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: '#fff',
    padding: theme.spacing(1, 2),
    borderRadius: theme.spacing(1),
    pointerEvents: 'none',
    zIndex: 1,
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  zoomHintText: {
    fontSize: '0.75rem',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  dialogFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(2, 3),

  },
  closeDialogButton: {
    textTransform: 'none',
    borderWidth: 1,
    minWidth: '120px',
    '&:hover': {
      borderWidth: 1,
    },
  },
}));
